import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import { CheckCircle2, XCircle, Loader2, Coins } from "lucide-react";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const { refreshUser } = useAuth();
  const { refresh: refreshCredits } = useCredits();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState({ kind: null, credits: null });

  useEffect(() => {
    if (!sessionId) { setStatus("timeout"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo({ kind: data.kind, credits: data.credits });
          setStatus("paid"); await refreshUser(); await refreshCredits(); return;
        }
      } catch (e) {}
      if (++tries >= 8) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, refreshUser, refreshCredits]);

  const isCredits = info.kind === "credit_pack";

  return (
    <div className="max-w-md mx-auto text-center py-24 px-6 rp-fade-up">
      {status === "checking" && <><Loader2 className="w-12 h-12 animate-spin text-[#C8000F] mx-auto mb-6" /><h1 className="font-display text-2xl font-bold">Confirming payment…</h1><p className="text-slate-500 mt-2">Hang tight, this takes a few seconds.</p></>}
      {status === "paid" && (isCredits ? (
        <><Coins className="w-16 h-16 text-[#E6B35A] mx-auto mb-6" /><h1 className="font-display text-3xl font-extrabold">Credits added!</h1><p className="text-slate-500 mt-2">{info.credits ? `${info.credits} credits are now in your wallet.` : "Your credits are in your wallet."}</p><button onClick={() => nav("/wallet")} data-testid="success-continue-btn" className="mt-8 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-6 py-3 rounded-lg">Go to Wallet</button></>
      ) : (
        <><CheckCircle2 className="w-16 h-16 text-[#5FA83B] mx-auto mb-6" /><h1 className="font-display text-3xl font-extrabold">You're Pro!</h1><p className="text-slate-500 mt-2">Route optimizer, trade alerts and reminders are unlocked.</p><button onClick={() => nav("/calendar")} data-testid="success-continue-btn" className="mt-8 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-6 py-3 rounded-lg">Back to Calendar</button></>
      ))}
      {status === "timeout" && <><Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-6" /><h1 className="font-display text-2xl font-bold">Still processing</h1><p className="text-slate-500 mt-2">Taking longer than expected. Refresh in a moment.</p><Link to="/wallet" className="mt-8 inline-block border border-[#E4E7EB] px-6 py-3 rounded-lg">Back to Wallet</Link></>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto text-center py-24 px-6 rp-fade-up">
      <XCircle className="w-16 h-16 text-[#C8000F] mx-auto mb-6" />
      <h1 className="font-display text-3xl font-extrabold">Checkout cancelled</h1>
      <p className="text-slate-500 mt-2">No charge was made. You can buy credits anytime.</p>
      <Link to="/wallet" data-testid="cancel-back-btn" className="mt-8 inline-block bg-[#C8000F] text-white font-semibold px-6 py-3 rounded-lg">Back to Wallet</Link>
    </div>
  );
}
